/** @jsxImportSource @emotion/react */
import { css } from "@emotion/react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Row,
  Space,
  Spin,
} from "antd";
import _ from "lodash";
import moment from "moment";
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { withRouter } from "react-router-dom";
import LicenseView from "src/containers/license/components/LicenseView";
import { useFormatMessage } from "src/hooks";
import messageIds from "src/locales/messageIds";
import { LicenseSeviceClient } from "src/services/LicenseService";

const formItemColor = css`
  display: flex;
  flex-direction: column;
  margin-bottom: 50px;
  line-height: 14px;
  color: rgb(61, 73, 102);
  font-weight: 600;
`;

const { RangePicker } = DatePicker;

export default React.memo(
  withRouter(function LicenseForm(props: any) {
    const { location } = props;
    const formatMessage = useFormatMessage();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [isView, setIsView] = useState(false);
    const [data, setData] = useState("");
    const [application, setApplication] = useState<number | undefined>(
      undefined
    );
    const [deploymentType, setDeploymentType] = useState<number | undefined>(
      undefined
    );
    const [systemAccount, setSystemAccount] = useState(1);
    const [licenseLocalOcr, setLicenseLocalOcr] = useState(false);

    useEffect(() => {
      form.resetFields();
      setApplication(undefined);
      setDeploymentType(undefined);
    }, [form, location.search]);

    const onValidateEmail = useCallback(
      (rule, value) => {
        const pattern = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z0-9_-]+)+$/;
        if (value) {
          if (pattern.test(_.trim(value))) {
            return Promise.resolve();
          } else {
            return Promise.reject(
              formatMessage(messageIds.aiManage.userManagment.validate.email)
            );
          }
        } else {
          return Promise.resolve();
        }
      },
      [formatMessage]
    );

    const onIssue = useCallback(async () => {
      const formContent = await form.validateFields();
      setLoading(true);
      const formData = {
        licensedToCompanyName: formContent.licensedToCompanyName,
        fromTo: [
          moment(formContent.fromTo[0]._d)
            .hours(0)
            .minutes(0)
            .second(0)
            .format("YYYY-MM-DDTHH:mm:ss+08:00"),
          moment(formContent.fromTo[1]._d)
            .hours(23)
            .minutes(59)
            .second(59)
            .format("YYYY-MM-DDTHH:mm:ss+08:00"),
        ],
        licenseType: location.search.endsWith("studio")
          ? "studio"
          : location.search.endsWith("premiumRobot")
          ? "robot-premium"
          : location.search.endsWith("localOCR")
          ? "ocr-local"
          : "console",
        application: formContent.application,
        note: formContent.note,
        applySource: 1,
        callbackEmail: _.trim(formContent.callbackEmail),
      };
      if (formContent.application === 3) {
        const formContractId = { contractId: formContent.contractId };
        Object.assign(formData, formContractId);
      }
      if (location.search.endsWith("console")) {
        const formEither = {
          deploymentType: formContent.deploymentType,
          licensedTo: formContent.licensedTo,
          numStudio: formContent.numStudio,
          numFloatingStudio: formContent.numFloatingStudio,
          numPremiumRobot: formContent.numPremiumRobot,
          numFloatingRobot: formContent.numFloatingRobot,
          numPdfImage: formContent.numPdfImage,
          numPdfText: formContent.numPdfText,
          numVicode: formContent.numVicode,
        };
        Object.assign(formData, formEither);
        if (deploymentType === 3) {
          const other = {
            machineKey: formContent.machineKey,
          };
          Object.assign(formData, other);
        } else {
          if (deploymentType === 1) {
            const other = {
              machineKey: formContent.licensedTo,
            };
            Object.assign(formData, other);
          }
        }
      } else if (
        location.search.endsWith("studio") ||
        location.search.endsWith("premiumRobot")
      ) {
        const formEither = {
          machineKey: formContent.machineKey,
          licensedTo: formContent.licensedToCompanyName,
          licenseLocalOcr: licenseLocalOcr,
        };
        Object.assign(formData, formEither);
      } else {
        const formEither = {
          machineKey: formContent.machineKey,
          licensedTo: formContent.licensedToCompanyName,
        };
        Object.assign(formData, formEither);
      }
      LicenseSeviceClient.signLicense(formData).then((res) => {
        if (res.data.success) {
          setData(res.data.data);
          message.success(
            formatMessage(messageIds.license.common.issueSuccessfully)
          );
          if (
            location.search.endsWith("studio") ||
            location.search.endsWith("premiumRobot") ||
            location.search.endsWith("localOCR")
          ) {
            form.resetFields(["machineKey"]);
            setLicenseLocalOcr(false);
          } else {
            form.resetFields();
            setApplication(undefined);
            setDeploymentType(undefined);
          }
          setIsView(true);
        } else {
          message.error(
            `${formatMessage(messageIds.license.common.issueFailed)}, ${
              res.data.message
            }`
          );
        }
        setLoading(false);
      });
    }, [deploymentType, form, formatMessage, licenseLocalOcr, location.search]);

    const onCancel = useCallback(() => {
      setIsView(!isView);
    }, [isView]);

    const context = useMemo(
      () => (
        <Fragment>
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: flex-end;
              margin-right: 30px;
              padding-bottom: 30px;
              border-bottom: 1px solid rgb(202, 209, 224);
            `}
          >
            <div
              css={css`
                max-width: 1000px;
                margin-right: 20px;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
              `}
            >
              <h1
                css={css`
                  position: relative;
                  margin: 0px 0px 8px;
                  padding-left: 10px;
                  line-height: 18px;
                  font-size: 18px;
                  font-weight: 600;
                  color: rgb(61, 73, 102);
                  ::before {
                    content: "";
                    position: absolute;
                    left: 0px;
                    width: 3px;
                    height: 18px;
                    background: rgb(51, 119, 255);
                  }
                `}
              >
                {location.search.endsWith("studio")
                  ? formatMessage(messageIds.menu.license.studio.name)
                  : location.search.endsWith("premiumRobot")
                  ? formatMessage(messageIds.menu.license.premiumRobot.name)
                  : location.search.endsWith("localOCR")
                  ? formatMessage(messageIds.menu.license.localOCR.name)
                  : formatMessage(messageIds.menu.license.console.name)}
              </h1>
            </div>
          </div>
          <div
            css={css`
              display: flex;
              justify-content: center;
              align-items: center;
              margin-top: 50px;
            `}
          >
            <div
              css={css`
                width: 150vh;
              `}
            >
              <Form
                form={form}
                layout="vertical"
                css={css`
                  width: 500;
                `}
              >
                <Row gutter={20}>
                  <Col span={24}>
                    <Form.Item
                      label={`${formatMessage(
                        messageIds.license.common.licenseType
                      )}：`}
                      css={formItemColor}
                    >
                      <div
                        css={css`
                          margin-top: 12px;
                          color: rgb(143, 155, 179);
                          background-color: rgb(247, 249, 252);
                          padding: 10px 13px;
                          min-height: 32px;
                        `}
                      >
                        {location.search.endsWith("studio")
                          ? formatMessage(messageIds.menu.license.studio.name)
                          : location.search.endsWith("premiumRobot")
                          ? formatMessage(
                              messageIds.menu.license.premiumRobot.name
                            )
                          : location.search.endsWith("localOCR")
                          ? formatMessage(messageIds.menu.license.localOCR.name)
                          : formatMessage(messageIds.menu.license.console.name)}
                      </div>
                    </Form.Item>
                  </Col>
                </Row>

                <Row>
                  <Col span={24}>
                    <Form.Item
                      label={`${formatMessage(
                        messageIds.license.common.licensedToCompanyName
                      )}：`}
                      name="licensedToCompanyName"
                      css={formItemColor}
                      rules={[
                        {
                          required: true,
                          message: formatMessage(
                            messageIds.license.validate.licensedToCompanyName
                          ),
                        },
                        {
                          whitespace: true,
                        },
                      ]}
                    >
                      <Input></Input>
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <Form.Item
                      label={`${formatMessage(
                        messageIds.license.common.purposeOfApplication
                      )}：`}
                      name="application"
                      css={formItemColor}
                      rules={[
                        {
                          required: true,
                          message: formatMessage(
                            messageIds.license.validate.purposeOfApplication
                          ),
                        },
                      ]}
                      // initialValue={application}
                    >
                      <Radio.Group
                        value={application}
                        onChange={(e) => {
                          setApplication(e.target.value);
                        }}
                      >
                        <Space direction="vertical">
                          <Radio value={1}>POC</Radio>
                          <Radio value={2}>
                            {formatMessage(messageIds.license.common.trial)}
                          </Radio>
                          <Radio value={3}>
                            {formatMessage(messageIds.license.common.formal)}
                          </Radio>
                          <Radio value={4}>
                            {formatMessage(messageIds.license.common.test)}
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>
                <Row>
                  {application === 3 ? (
                    <Col span={24}>
                      <Form.Item
                        label={`${formatMessage(
                          messageIds.license.common.contractId
                        )}：`}
                        name="contractId"
                        css={formItemColor}
                        rules={[
                          {
                            required: true,
                            message: formatMessage(
                              messageIds.license.validate.contractId
                            ),
                          },
                          {
                            whitespace: true,
                          },
                        ]}
                      >
                        <Input></Input>
                      </Form.Item>
                    </Col>
                  ) : (
                    ""
                  )}
                </Row>
                <Row>
                  {location.search.endsWith("console") ? (
                    <Col>
                      <Form.Item
                        label={`${formatMessage(
                          messageIds.license.common.deploymentWay
                        )}：`}
                        name="deploymentType"
                        css={formItemColor}
                        rules={[
                          {
                            required: true,
                            message: formatMessage(
                              messageIds.license.validate.deploymentWay
                            ),
                          },
                        ]}
                        // initialValue={deploymentType}
                      >
                        <Radio.Group
                          value={deploymentType}
                          onChange={(e) => {
                            setDeploymentType(e.target.value);
                          }}
                        >
                          <Space direction="vertical">
                            <Radio value={1}>
                              {formatMessage(
                                messageIds.license.common.saasVersion
                              )}
                            </Radio>
                            <Radio value={3}>
                              {formatMessage(
                                messageIds.license.common.privatization
                              )}
                            </Radio>
                          </Space>
                        </Radio.Group>
                      </Form.Item>
                    </Col>
                  ) : (
                    ""
                  )}
                </Row>
                <Row gutter={70}>
                  {location.search.endsWith("console") ? (
                    deploymentType === 1 ? (
                      <Fragment>
                        <Col>
                          <Form.Item
                            label={`${formatMessage(
                              messageIds.license.common.systemAccount
                            )}：`}
                            name="systemAccount"
                            css={formItemColor}
                            rules={[
                              {
                                required: true,
                                message: formatMessage(
                                  messageIds.license.validate.systemAccount
                                ),
                              },
                            ]}
                            initialValue={systemAccount}
                          >
                            <Radio.Group
                              value={systemAccount}
                              onChange={(e) => {
                                setSystemAccount(e.target.value);
                              }}
                            >
                              <Space direction="vertical">
                                <Radio value={1}>
                                  {formatMessage(
                                    messageIds.license.common.existingCompany
                                  )}
                                </Radio>
                              </Space>
                            </Radio.Group>
                          </Form.Item>
                        </Col>
                      </Fragment>
                    ) : deploymentType === 3 ? (
                      <Fragment>
                        <Col span={12}>
                          <Form.Item
                            label={`${formatMessage(
                              messageIds.license.common.licensedTo
                            )}：`}
                            name="licensedTo"
                            css={formItemColor}
                            rules={[
                              {
                                required: true,
                                message: formatMessage(
                                  messageIds.license.validate.licensedTo
                                ),
                              },
                              {
                                whitespace: true,
                              },
                            ]}
                          >
                            <Input></Input>
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            label={`${formatMessage(
                              messageIds.license.common.machineKey
                            )}：`}
                            name="machineKey"
                            css={formItemColor}
                            rules={[
                              {
                                required: true,
                                message: formatMessage(
                                  messageIds.license.validate.machineKey
                                ),
                              },
                              {
                                whitespace: true,
                              },
                            ]}
                          >
                            <Input></Input>
                          </Form.Item>
                        </Col>
                      </Fragment>
                    ) : (
                      ""
                    )
                  ) : (
                    <Col span={24}>
                      <Form.Item
                        label={`${formatMessage(
                          messageIds.license.common.machineKey
                        )}：`}
                        name="machineKey"
                        css={formItemColor}
                        rules={[
                          {
                            required: true,
                            message: formatMessage(
                              messageIds.license.validate.machineKey
                            ),
                          },
                          {
                            whitespace: true,
                          },
                        ]}
                      >
                        <Input></Input>
                      </Form.Item>
                    </Col>
                  )}
                </Row>
                <Row>
                  {deploymentType === 1 && systemAccount === 1 ? (
                    <Col span={24}>
                      <Form.Item
                        label={`${formatMessage(
                          messageIds.license.common.licensedTo
                        )}：`}
                        name="licensedTo"
                        css={formItemColor}
                        rules={[
                          {
                            required: true,
                            message: formatMessage(
                              messageIds.license.validate.licensedTo
                            ),
                          },
                          {
                            whitespace: true,
                          },
                        ]}
                      >
                        <Input></Input>
                      </Form.Item>
                    </Col>
                  ) : (
                    ""
                  )}
                </Row>
                <Row gutter={70}>
                  <Col span={24}>
                    <Form.Item
                      label={`${formatMessage(
                        messageIds.license.common.fromTo
                      )}：`}
                      name="fromTo"
                      css={formItemColor}
                      rules={[
                        {
                          required: true,
                          message: formatMessage(
                            messageIds.license.validate.fromTo
                          ),
                        },
                      ]}
                      initialValue={[moment(new Date()), moment(new Date())]}
                    >
                      <RangePicker
                        format="YYYY/MM/DD"
                        css={css`
                          width: 100%;
                        `}
                      ></RangePicker>
                    </Form.Item>
                  </Col>
                </Row>
                {location.search.endsWith("console") ? (
                  <Fragment>
                    <Row gutter={70}>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numStudio
                          )}：`}
                          name="numStudio"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numStudio
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numFloatingStudio
                          )}：`}
                          name="numFloatingStudio"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numFloatingStudio
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={70}>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numPremiumRobot
                          )}：`}
                          name="numPremiumRobot"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numPremiumRobot
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numFloatingRobot
                          )}：`}
                          name="numFloatingRobot"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numFloatingRobot
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={70}>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numPdfImage
                          )}：`}
                          name="numPdfImage"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numPdfImage
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numPdfText
                          )}：`}
                          name="numPdfText"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numPdfText
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row gutter={70}>
                      <Col span={12}>
                        <Form.Item
                          label={`${formatMessage(
                            messageIds.license.console.numVicode
                          )}：`}
                          name="numVicode"
                          css={formItemColor}
                          rules={[
                            {
                              required: true,
                              message: formatMessage(
                                messageIds.license.validate.numVicode
                              ),
                            },
                          ]}
                          initialValue={0}
                        >
                          <InputNumber
                            placeholder={`${formatMessage(
                              messageIds.common.minimum
                            )}：0, ${formatMessage(
                              messageIds.common.maximum
                            )}: 2147483647`}
                            min={0}
                            max={2147483647}
                            css={css`
                              width: 100%;
                            `}
                          ></InputNumber>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Fragment>
                ) : (
                  ""
                )}
                <Row gutter={70}>
                  <Col span={24}>
                    <Form.Item
                      label={`${formatMessage(
                        messageIds.license.common.notice
                      )}：`}
                      name="callbackEmail"
                      css={formItemColor}
                      rules={[
                        {
                          validator: onValidateEmail,
                          message: formatMessage(
                            messageIds.aiManage.userManagment.validate.email
                          ),
                          validateTrigger: "blur",
                        },
                      ]}
                    >
                      <Input></Input>
                    </Form.Item>
                  </Col>
                </Row>
                {(location.search.endsWith("studio") ||
                  location.search.endsWith("premiumRobot")) && (
                  <Row gutter={70}>
                    <Col span={24}>
                      <Form.Item name="licenseLocalOcr" css={formItemColor}>
                        <Checkbox
                          checked={licenseLocalOcr}
                          onChange={(e) => setLicenseLocalOcr(e.target.checked)}
                        >
                          {formatMessage(messageIds.license.common.localOcr)}
                        </Checkbox>
                      </Form.Item>
                    </Col>
                  </Row>
                )}
                <Row gutter={70}>
                  <Col span={24}>
                    <Form.Item
                      label={`${formatMessage(
                        messageIds.common.description
                      )}：`}
                      name="note"
                      css={formItemColor}
                    >
                      <Input.TextArea rows={5}></Input.TextArea>
                    </Form.Item>
                  </Col>
                </Row>
              </Form>
              <div>
                <Button
                  type="primary"
                  css={css`
                    padding: 4px 15px;
                    width: 107;
                    font-size: 15px;
                    border: 1px solid rgb(51, 119, 255);
                    border-radius: 4px;
                    height: auto;
                  `}
                  onClick={onIssue}
                >
                  {formatMessage(messageIds.common.issueOfPermit)}
                </Button>
              </div>
            </div>
          </div>
        </Fragment>
      ),
      [
        application,
        deploymentType,
        form,
        formatMessage,
        licenseLocalOcr,
        location.search,
        onIssue,
        onValidateEmail,
        systemAccount,
      ]
    );

    return (
      <div
        css={css`
          flex-grow: 1;
          padding: 30px;
        `}
      >
        {loading ? (
          <Spin
            tip="Loading..."
            css={css`
              position: initial !important;
            `}
          >
            {context}
          </Spin>
        ) : (
          context
        )}
        <LicenseView
          isView={isView}
          content={data}
          onCancel={onCancel}
        ></LicenseView>
      </div>
    );
  })
);
